import { useState } from 'react';
import { ChevronRight, ChevronLeft, ShoppingBag, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import Footer from '../components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const menuData = {
  cupcakes: {
    baseFlavors: [
      'Vanilla', 'Chocolate', 'Red Velvet', 'Carrot Cake', 'Funfetti',
      'Cinnamon Toast', 'Strawberry', 'Mocha', 'Oreo', 'Peanut Butter',
      'Chai Latte', 'German Chocolate', 'Pineapple', 'Lemon', 'Custom (+$1)'
    ],
    fillings: [
      'Strawberry', 'Raspberry', 'Pineapple', 'Lemon', 'Blueberry', 'Cinnamon',
      'Nutella', 'Peanut Butter', 'Cookie Butter', 'Caramel', 'Cream', 'Coconut Pecan',
      'Chocolate', 'White Chocolate', 'Mint', 'Coffee', 'Oreo', 'Custom (+$1)'
    ],
    specialtyNonDecorative: [
      'Crème Brûlée Cupcakes', 'Dubai Chocolate Cupcakes', 'Churro Cupcakes',
      'Dulce De Leche Cupcakes', 'Samoa Cupcakes', 'Butterfinger Cupcakes',
      'Tres Leches Cupcakes', 'Dark Chocolate Turtle Cupcakes', 'Smores Cupcakes',
      'Tiramisu Cupcakes'
    ],
    quantities: {
      single: {
        options: [
          { name: 'Classic Decorative Flowers (1 color)', price: 4.50 },
          { name: 'Specialty Decorative Flowers (2-3 colors)', price: 5.50 },
          { name: 'Custom Decor Cupcake', price: 6.50 }
        ],
        upgrades: [
          { name: 'Jumbo', price: 1.50 },
          { name: 'Filling', price: 1.50 }
        ]
      },
      six: {
        options: [
          { name: 'Classic (3 colors total)', price: 25.00 },
          { name: 'Specialty (6-9 colors total)', price: 30.00 },
          { name: 'Custom Theme', price: 40.00 }
        ],
        upgrades: [
          { name: 'Jumbo', price: 10.00 },
          { name: 'Filling', price: 10.00 }
        ]
      },
      twelve: {
        options: [
          { name: 'Classic (3-4 colors total)', price: 50.00 },
          { name: 'Specialty (6-9 colors total)', price: 60.00 },
          { name: 'Custom Theme', price: 80.00 }
        ],
        upgrades: [
          { name: 'Jumbo', price: 20.00 },
          { name: 'Filling', price: 20.00 }
        ]
      }
    }
  },
  cakes: {
    flavors: [
      'Vanilla', 'Chocolate', 'Red Velvet', 'Carrot Cake', 'Funfetti',
      'Cinnamon Toast', 'Strawberry', 'Mocha', 'Oreo', 'Peanut Butter',
      'Chai Latte', 'German Chocolate', 'Pineapple', 'Coffee', 'Custom (+$5)'
    ],
    icings: {
      standard: [
        'Vanilla Bean Buttercream', 'Cream Cheese Frosting',
        'Chocolate Buttercream', 'Whipped Vanilla Cream'
      ],
      special: [
        'Chocolate Ganache (+$3.50)', 'Brown Sugar Buttercream (+$3.50)',
        'Cinnamon Buttercream (+$3.50)', 'Peanut Butter Cream Icing (+$3.50)',
        'Strawberry Buttercream (+$3.50)', 'Oreo Frosting (+$3.50)',
        'Coconut Pecan Icing (+$3.50)', 'White Chocolate Ganache (+$3.50)'
      ]
    },
    sizes: [
      { name: '6" Two Layers', price: 40 },
      { name: '6" Three Layers', price: 50 },
      { name: '8" Two Layers', price: 50 },
      { name: '8" Three Layers', price: 60 }
    ],
    decorations: [
      { name: 'Simple', price: 5 },
      { name: 'Decorative', price: 10 },
      { name: 'Full Floral', price: 35 },
      { name: 'Custom', price: 0, note: 'Negotiable' }
    ]
  },
  cookies: {
    flavors: [
      'Classic Chocolate Chip', 'Snickerdoodle', 'Sugar Cookies', 'Red Velvet',
      'Cookies and Cream', 'Chocolate Cherry', 'Lemon Raspberry', 'Crème Brûlée',
      'Strawberry Matcha', 'Peanut Butter Cup', 'Dulce De Leche', 'Blackberry',
      'Dubai Chocolate', 'Peach Cobbler', 'Brownie'
    ],
    quantities: [
      { name: 'Single Cookie', price: 4.00 },
      { name: '6 Count Box', price: 22.00 },
      { name: '12 Count Box', price: 40.00 }
    ]
  },
  cakePops: {
    flavors: [
      'Vanilla', 'Chocolate', 'Red Velvet', 'Carrot Cake', 'Funfetti',
      'Cinnamon Toast', 'Strawberry', 'Mocha', 'Oreo', 'Peanut Butter',
      'Chai Latte', 'GingerBread', 'Pineapple', 'Lemon', 'Custom (+$1)'
    ],
    quantities: [
      { name: 'Single Classic', price: 2.50 },
      { name: 'Single Custom', price: 3.50 },
      { name: '6 Classic', price: 15.00 },
      { name: '6 Custom', price: 20.00 },
      { name: '12 Classic', price: 26.00 },
      { name: '12 Custom', price: 36.00 }
    ]
  }
};

export default function Ordering() {
  const [step, setStep] = useState(1);
  const [orderData, setOrderData] = useState({
    category: '',
    flavor: '',
    filling: '',
    icing: '',
    quantity: '',
    size: '',
    decoration: '',
    customization: '',
    colorTheme: '',
    allergyNotes: '',
    eventType: '',
    pickupDate: '',
    pickupTime: '',
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    price: 0
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderConfirmed, setOrderConfirmed] = useState(false);

  const calculatePrice = (data) => {
    let price = 0;
    
    if (data.category === 'cupcakes') {
      if (menuData.cupcakes.specialtyNonDecorative.includes(data.flavor)) {
        price = 6.00;
      } else {
        const quantityType = data.quantity === 'single' ? 'single' : data.quantity === 'six' ? 'six' : 'twelve';
        const option = menuData.cupcakes.quantities[quantityType]?.options.find(o => o.name === data.size);
        if (option) price = option.price;
      }
      if (data.flavor.includes('Custom')) price += data.quantity === 'single' ? 1 : data.quantity === 'six' ? 6 : 12;
      if (data.filling && data.filling !== 'None') {
        const quantityType = data.quantity === 'single' ? 'single' : data.quantity === 'six' ? 'six' : 'twelve';
        const fillingUpgrade = menuData.cupcakes.quantities[quantityType]?.upgrades.find(u => u.name === 'Filling');
        if (fillingUpgrade) price += fillingUpgrade.price;
      }
    } else if (data.category === 'cakes') {
      const sizeOption = menuData.cakes.sizes.find(s => s.name === data.size);
      if (sizeOption) price += sizeOption.price;
      if (data.icing?.includes('+$3.50')) price += 3.50;
      if (data.flavor?.includes('Custom')) price += 5;
      const decoOption = menuData.cakes.decorations.find(d => d.name === data.decoration);
      if (decoOption && decoOption.price) price += decoOption.price;
    } else if (data.category === 'cookies') {
      const qty = menuData.cookies.quantities.find(q => q.name === data.quantity);
      if (qty) price = qty.price;
    } else if (data.category === 'cakePops') {
      const qty = menuData.cakePops.quantities.find(q => q.name === data.quantity);
      if (qty) price = qty.price;
      if (data.flavor?.includes('Custom')) {
        if (data.quantity === 'Single Classic' || data.quantity === 'Single Custom') price += 1;
        else if (data.quantity.startsWith('6')) price += 6;
        else if (data.quantity.startsWith('12')) price += 12;
      }
    }
    
    return price;
  };

  const handleNext = () => {
    if (step === 1 && !orderData.category) {
      toast.error('Please select a dessert category');
      return;
    }
    if (step === 2 && !orderData.flavor) {
      toast.error('Please select a flavor');
      return;
    }
    if (step === 3 && !orderData.quantity) {
      toast.error('Please select a quantity');
      return;
    }
    if (step === 5 && (!orderData.pickupDate || !orderData.pickupTime)) {
      toast.error('Please select pickup date and time');
      return;
    }
    
    const newPrice = calculatePrice(orderData);
    setOrderData({...orderData, price: newPrice});
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!orderData.customerName || !orderData.customerEmail || !orderData.customerPhone) {
      toast.error('Please fill in all contact information');
      return;
    }

    setIsSubmitting(true);
    try {
      const finalPrice = calculatePrice(orderData);
      const orderPayload = {
        items: [{
          category: orderData.category,
          flavor: orderData.flavor,
          filling: orderData.filling || null,
          icing: orderData.icing || null,
          quantity: orderData.quantity,
          size: orderData.size || null,
          decoration: orderData.decoration || null,
          price: finalPrice
        }],
        customization: orderData.customization || null,
        color_theme: orderData.colorTheme || null,
        allergy_notes: orderData.allergyNotes || null,
        event_type: orderData.eventType || null,
        pickup_date: orderData.pickupDate,
        pickup_time: orderData.pickupTime,
        customer_name: orderData.customerName,
        customer_email: orderData.customerEmail,
        customer_phone: orderData.customerPhone,
        total_price: finalPrice
      };

      await axios.post(`${API}/orders`, orderPayload);
      toast.success('Order submitted successfully!');
      setOrderConfirmed(true);
      setStep(7);
    } catch (error) {
      console.error('Order submission error:', error);
      toast.error('Failed to submit order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetOrder = () => {
    setOrderData({
      category: '', flavor: '', filling: '', icing: '', quantity: '', size: '',
      decoration: '', customization: '', colorTheme: '', allergyNotes: '',
      eventType: '', pickupDate: '', pickupTime: '', customerName: '',
      customerEmail: '', customerPhone: '', price: 0
    });
    setStep(1);
    setOrderConfirmed(false);
  };

  if (orderConfirmed && step === 7) {
    return (
      <div className="min-h-screen pt-20 pb-16 px-6" data-testid="order-confirmation">
        <div className="max-w-2xl mx-auto text-center py-16">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Check className="text-green-600" size={48} />
          </div>
          <h1 className="text-4xl mb-4">Order Received!</h1>
          <p className="text-[var(--text-muted)] text-lg mb-8 leading-relaxed">
            Thank you for your order! We've received your request and will be in touch soon 
            to confirm the details. A confirmation email has been sent to {orderData.customerEmail}.
          </p>
          <button
            onClick={resetOrder}
            className="px-10 py-4 bg-[var(--primary)] text-[var(--primary-foreground)] rounded-full text-lg font-medium button-hover"
            data-testid="new-order-button"
          >
            Place Another Order
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20" data-testid="ordering-page">
      <div className="max-w-5xl mx-auto px-6 lg:px-12 py-12">
        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-4">
            {[1, 2, 3, 4, 5, 6].map((s) => (
              <div key={s} className="flex items-center">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
                  step >= s ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'bg-gray-200 text-gray-500'
                }`}>
                  {s}
                </div>
                {s < 6 && <div className={`w-full h-1 ${step > s ? 'bg-[var(--primary)]' : 'bg-gray-200'}`} style={{width: '60px'}} />}
              </div>
            ))}
          </div>
          <div className="text-center text-sm text-[var(--text-muted)]">
            {step === 1 && 'Choose Category'}
            {step === 2 && 'Select Flavor'}
            {step === 3 && 'Quantity & Size'}
            {step === 4 && 'Customization'}
            {step === 5 && 'Pickup Details'}
            {step === 6 && 'Review Order'}
          </div>
        </div>

        {/* Step Content */}
        <div className="bg-white rounded-3xl p-8 lg:p-12 border border-[var(--border-soft)] min-h-[500px]">
          {/* Step 1: Category */}
          {step === 1 && (
            <div data-testid="step-category">
              <h2 className="text-3xl mb-8 text-center">Choose Your Dessert</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {['Cakes', 'Cupcakes', 'Cookies', 'Cake Pops'].map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setOrderData({...orderData, category: cat.toLowerCase().replace(' ', '')})}
                    data-testid={`category-${cat.toLowerCase().replace(' ', '-')}`}
                    className={`p-8 rounded-2xl border-2 text-center button-hover ${
                      orderData.category === cat.toLowerCase().replace(' ', '')
                        ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                        : 'border-[var(--border-soft)]'
                    }`}
                  >
                    <div className="text-5xl mb-4">
                      {cat === 'Cakes' && '🎂'}
                      {cat === 'Cupcakes' && '🧁'}
                      {cat === 'Cookies' && '🍪'}
                      {cat === 'Cake Pops' && '🍭'}
                    </div>
                    <h3 className="text-2xl font-medium">{cat}</h3>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Flavor */}
          {step === 2 && (
            <div data-testid="step-flavor">
              <h2 className="text-3xl mb-8 text-center">Select Flavor</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 max-h-[500px] overflow-y-auto">
                {orderData.category === 'cupcakes' && (
                  <>
                    {menuData.cupcakes.baseFlavors.map((flavor) => (
                      <button
                        key={flavor}
                        onClick={() => setOrderData({...orderData, flavor})}
                        data-testid={`flavor-${flavor.toLowerCase().replace(/\s/g, '-')}`}
                        className={`p-4 rounded-xl border-2 text-left button-hover ${
                          orderData.flavor === flavor
                            ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                            : 'border-[var(--border-soft)]'
                        }`}
                      >
                        {flavor}
                      </button>
                    ))}
                    <div className="col-span-full mt-4">
                      <h3 className="text-xl mb-4 font-medium">Specialty Non-Decorative ($6.00)</h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {menuData.cupcakes.specialtyNonDecorative.map((flavor) => (
                          <button
                            key={flavor}
                            onClick={() => setOrderData({...orderData, flavor})}
                            data-testid={`specialty-${flavor.toLowerCase().replace(/\s/g, '-')}`}
                            className={`p-4 rounded-xl border-2 text-left button-hover ${
                              orderData.flavor === flavor
                                ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                                : 'border-[var(--border-soft)]'
                            }`}
                          >
                            {flavor}
                          </button>
                        ))}
                      </div>
                    </div>
                  </>
                )}
                {orderData.category === 'cakes' && menuData.cakes.flavors.map((flavor) => (
                  <button
                    key={flavor}
                    onClick={() => setOrderData({...orderData, flavor})}
                    data-testid={`cake-flavor-${flavor.toLowerCase().replace(/\s/g, '-')}`}
                    className={`p-4 rounded-xl border-2 text-left button-hover ${
                      orderData.flavor === flavor
                        ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                        : 'border-[var(--border-soft)]'
                    }`}
                  >
                    {flavor}
                  </button>
                ))}
                {orderData.category === 'cookies' && menuData.cookies.flavors.map((flavor) => (
                  <button
                    key={flavor}
                    onClick={() => setOrderData({...orderData, flavor})}
                    data-testid={`cookie-flavor-${flavor.toLowerCase().replace(/\s/g, '-')}`}
                    className={`p-4 rounded-xl border-2 text-left button-hover ${
                      orderData.flavor === flavor
                        ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                        : 'border-[var(--border-soft)]'
                    }`}
                  >
                    {flavor}
                  </button>
                ))}
                {orderData.category === 'cakepops' && menuData.cakePops.flavors.map((flavor) => (
                  <button
                    key={flavor}
                    onClick={() => setOrderData({...orderData, flavor})}
                    data-testid={`cakepop-flavor-${flavor.toLowerCase().replace(/\s/g, '-')}`}
                    className={`p-4 rounded-xl border-2 text-left button-hover ${
                      orderData.flavor === flavor
                        ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                        : 'border-[var(--border-soft)]'
                    }`}
                  >
                    {flavor}
                  </button>
                ))}
              </div>
              
              {/* Conditional: Filling for Cupcakes or Icing for Cakes */}
              {orderData.category === 'cupcakes' && orderData.flavor && !menuData.cupcakes.specialtyNonDecorative.includes(orderData.flavor) && (
                <div className="mt-8 pt-8 border-t border-[var(--border-soft)]">
                  <h3 className="text-xl mb-4 font-medium">Add Filling? (+$1.50 per upgrade)</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 max-h-60 overflow-y-auto mb-4">
                    <button
                      onClick={() => setOrderData({...orderData, filling: 'None'})}
                      className={`p-3 rounded-xl border-2 text-sm ${
                        orderData.filling === 'None'
                          ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                          : 'border-[var(--border-soft)]'
                      }`}
                      data-testid="filling-none"
                    >
                      Skip Filling
                    </button>
                    {menuData.cupcakes.fillings.map((filling) => (
                      <button
                        key={filling}
                        onClick={() => setOrderData({...orderData, filling})}
                        data-testid={`filling-${filling.toLowerCase().replace(/\s/g, '-')}`}
                        className={`p-3 rounded-xl border-2 text-sm ${
                          orderData.filling === filling
                            ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                            : 'border-[var(--border-soft)]'
                        }`}
                      >
                        {filling}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {orderData.category === 'cakes' && orderData.flavor && (
                <div className="mt-8 pt-8 border-t border-[var(--border-soft)]">
                  <h3 className="text-xl mb-4 font-medium">Choose Icing</h3>
                  <p className="text-sm text-[var(--text-muted)] mb-4">
                    These icing flavors are used between cake layers unless otherwise requested.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-3">Standard</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {menuData.cakes.icings.standard.map((icing) => (
                          <button
                            key={icing}
                            onClick={() => setOrderData({...orderData, icing})}
                            data-testid={`icing-${icing.toLowerCase().replace(/\s/g, '-')}`}
                            className={`p-3 rounded-xl border-2 text-left ${
                              orderData.icing === icing
                                ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                                : 'border-[var(--border-soft)]'
                            }`}
                          >
                            {icing}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h4 className="font-medium mb-3">Special (+$3.50)</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {menuData.cakes.icings.special.map((icing) => (
                          <button
                            key={icing}
                            onClick={() => setOrderData({...orderData, icing})}
                            data-testid={`special-icing-${icing.toLowerCase().replace(/\s/g, '-')}`}
                            className={`p-3 rounded-xl border-2 text-left ${
                              orderData.icing === icing
                                ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                                : 'border-[var(--border-soft)]'
                            }`}
                          >
                            {icing}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Quantity & Size */}
          {step === 3 && (
            <div data-testid="step-quantity">
              <h2 className="text-3xl mb-8 text-center">Select Quantity & Size</h2>
              {orderData.category === 'cupcakes' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl mb-4 font-medium">Choose Quantity</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
                      {['single', 'six', 'twelve'].map((qty) => (
                        <button
                          key={qty}
                          onClick={() => setOrderData({...orderData, quantity: qty, size: ''})}
                          data-testid={`quantity-${qty}`}
                          className={`p-6 rounded-xl border-2 button-hover ${
                            orderData.quantity === qty
                              ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                              : 'border-[var(--border-soft)]'
                          }`}
                        >
                          <div className="text-2xl font-bold mb-2 capitalize">{qty}</div>
                          <div className="text-sm text-[var(--text-muted)]">
                            {qty === 'single' && '1 Cupcake'}
                            {qty === 'six' && '6 Count Box'}
                            {qty === 'twelve' && '12 Count Box'}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {orderData.quantity && (
                    <div>
                      <h3 className="text-xl mb-4 font-medium">Choose Style</h3>
                      <div className="grid grid-cols-1 gap-4">
                        {menuData.cupcakes.quantities[orderData.quantity]?.options.map((option) => (
                          <button
                            key={option.name}
                            onClick={() => setOrderData({...orderData, size: option.name})}
                            data-testid={`style-${option.name.toLowerCase().replace(/\s/g, '-')}`}
                            className={`p-4 rounded-xl border-2 text-left flex justify-between items-center button-hover ${
                              orderData.size === option.name
                                ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                                : 'border-[var(--border-soft)]'
                            }`}
                          >
                            <span>{option.name}</span>
                            <span className="font-semibold text-[var(--accent-gold)]">${option.price.toFixed(2)}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {orderData.category === 'cakes' && (
                <div className="space-y-6">
                  <div>
                    <h3 className="text-xl mb-4 font-medium">Choose Size</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {menuData.cakes.sizes.map((size) => (
                        <button
                          key={size.name}
                          onClick={() => setOrderData({...orderData, size: size.name, quantity: size.name})}
                          data-testid={`cake-size-${size.name.toLowerCase().replace(/\s/g, '-')}`}
                          className={`p-6 rounded-xl border-2 text-left button-hover ${
                            orderData.size === size.name
                              ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                              : 'border-[var(--border-soft)]'
                          }`}
                        >
                          <div className="font-semibold mb-2">{size.name}</div>
                          <div className="text-[var(--accent-gold)] font-bold">${size.price}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h3 className="text-xl mb-4 font-medium">Choose Decoration Style</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {menuData.cakes.decorations.map((deco) => (
                        <button
                          key={deco.name}
                          onClick={() => setOrderData({...orderData, decoration: deco.name})}
                          data-testid={`decoration-${deco.name.toLowerCase()}`}
                          className={`p-4 rounded-xl border-2 text-left flex justify-between items-center button-hover ${
                            orderData.decoration === deco.name
                              ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                              : 'border-[var(--border-soft)]'
                          }`}
                        >
                          <span>{deco.name}</span>
                          <span className="font-semibold text-[var(--accent-gold)]">
                            {deco.note || `+$${deco.price}`}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
              
              {orderData.category === 'cookies' && (
                <div className="grid grid-cols-1 gap-4">
                  {menuData.cookies.quantities.map((qty) => (
                    <button
                      key={qty.name}
                      onClick={() => setOrderData({...orderData, quantity: qty.name})}
                      data-testid={`cookie-quantity-${qty.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`p-6 rounded-xl border-2 text-left flex justify-between items-center button-hover ${
                        orderData.quantity === qty.name
                          ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                          : 'border-[var(--border-soft)]'
                      }`}
                    >
                      <span className="font-semibold">{qty.name}</span>
                      <span className="text-[var(--accent-gold)] font-bold">${qty.price.toFixed(2)}</span>
                    </button>
                  ))}
                </div>
              )}
              
              {orderData.category === 'cakepops' && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {menuData.cakePops.quantities.map((qty) => (
                    <button
                      key={qty.name}
                      onClick={() => setOrderData({...orderData, quantity: qty.name})}
                      data-testid={`cakepop-quantity-${qty.name.toLowerCase().replace(/\s/g, '-')}`}
                      className={`p-6 rounded-xl border-2 text-left button-hover ${
                        orderData.quantity === qty.name
                          ? 'border-[var(--accent-gold)] bg-[var(--primary)]/10'
                          : 'border-[var(--border-soft)]'
                      }`}
                    >
                      <div className="font-semibold mb-2">{qty.name}</div>
                      <div className="text-[var(--accent-gold)] font-bold">${qty.price.toFixed(2)}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Step 4: Customization */}
          {step === 4 && (
            <div data-testid="step-customization">
              <h2 className="text-3xl mb-8 text-center">Customize Your Order</h2>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium mb-2">Color Palette / Theme</label>
                  <input
                    type="text"
                    value={orderData.colorTheme}
                    onChange={(e) => setOrderData({...orderData, colorTheme: e.target.value})}
                    placeholder="e.g., Pink and Gold, Pastel Rainbow"
                    data-testid="color-theme-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Special Requests & Design Ideas</label>
                  <textarea
                    value={orderData.customization}
                    onChange={(e) => setOrderData({...orderData, customization: e.target.value})}
                    placeholder="Describe your vision, special decorations, or any custom requests..."
                    data-testid="customization-input"
                    rows={4}
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Allergy Notes</label>
                  <input
                    type="text"
                    value={orderData.allergyNotes}
                    onChange={(e) => setOrderData({...orderData, allergyNotes: e.target.value})}
                    placeholder="Any allergies or dietary restrictions?"
                    data-testid="allergy-notes-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Event Type (Optional)</label>
                  <input
                    type="text"
                    value={orderData.eventType}
                    onChange={(e) => setOrderData({...orderData, eventType: e.target.value})}
                    placeholder="e.g., Birthday, Wedding, Baby Shower"
                    data-testid="event-type-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div className="bg-[var(--background-secondary)] p-6 rounded-2xl">
                  <p className="text-sm text-[var(--text-muted)] leading-relaxed">
                    💡 <strong>Tip:</strong> You can upload inspiration photos later or share them with us 
                    via email after placing your order.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Step 5: Pickup Details */}
          {step === 5 && (
            <div data-testid="step-pickup">
              <h2 className="text-3xl mb-8 text-center">Pickup Details</h2>
              <div className="space-y-6 max-w-2xl mx-auto">
                <div>
                  <label className="block text-sm font-medium mb-2">Pickup Date *</label>
                  <input
                    type="date"
                    value={orderData.pickupDate}
                    onChange={(e) => setOrderData({...orderData, pickupDate: e.target.value})}
                    data-testid="pickup-date-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Pickup Time *</label>
                  <input
                    type="time"
                    value={orderData.pickupTime}
                    onChange={(e) => setOrderData({...orderData, pickupTime: e.target.value})}
                    data-testid="pickup-time-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Your Name *</label>
                  <input
                    type="text"
                    value={orderData.customerName}
                    onChange={(e) => setOrderData({...orderData, customerName: e.target.value})}
                    placeholder="Full Name"
                    data-testid="customer-name-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Email Address *</label>
                  <input
                    type="email"
                    value={orderData.customerEmail}
                    onChange={(e) => setOrderData({...orderData, customerEmail: e.target.value})}
                    placeholder="your@email.com"
                    data-testid="customer-email-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Phone Number *</label>
                  <input
                    type="tel"
                    value={orderData.customerPhone}
                    onChange={(e) => setOrderData({...orderData, customerPhone: e.target.value})}
                    placeholder="(123) 456-7890"
                    data-testid="customer-phone-input"
                    className="w-full p-4 rounded-2xl border-2 border-[var(--border-soft)] focus:border-[var(--accent-gold)] focus:outline-none"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Review */}
          {step === 6 && (
            <div data-testid="step-review">
              <h2 className="text-3xl mb-8 text-center">Review Your Order</h2>
              <div className="space-y-6 max-w-2xl mx-auto">
                <div className="bg-[var(--background-secondary)] p-6 rounded-2xl space-y-4">
                  <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                    <span className="font-medium">Category:</span>
                    <span className="capitalize">{orderData.category}</span>
                  </div>
                  <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                    <span className="font-medium">Flavor:</span>
                    <span>{orderData.flavor}</span>
                  </div>
                  {orderData.filling && orderData.filling !== 'None' && (
                    <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                      <span className="font-medium">Filling:</span>
                      <span>{orderData.filling}</span>
                    </div>
                  )}
                  {orderData.icing && (
                    <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                      <span className="font-medium">Icing:</span>
                      <span>{orderData.icing}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                    <span className="font-medium">Quantity:</span>
                    <span className="capitalize">{orderData.quantity}</span>
                  </div>
                  {orderData.size && (
                    <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                      <span className="font-medium">Size/Style:</span>
                      <span>{orderData.size}</span>
                    </div>
                  )}
                  {orderData.decoration && (
                    <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                      <span className="font-medium">Decoration:</span>
                      <span>{orderData.decoration}</span>
                    </div>
                  )}
                  {orderData.colorTheme && (
                    <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                      <span className="font-medium">Color Theme:</span>
                      <span>{orderData.colorTheme}</span>
                    </div>
                  )}
                  {orderData.customization && (
                    <div className="border-b border-[var(--border-soft)] pb-3">
                      <span className="font-medium">Custom Requests:</span>
                      <p className="mt-2 text-sm text-[var(--text-muted)]">{orderData.customization}</p>
                    </div>
                  )}
                  <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                    <span className="font-medium">Pickup:</span>
                    <span>{orderData.pickupDate} at {orderData.pickupTime}</span>
                  </div>
                  <div className="flex justify-between border-b border-[var(--border-soft)] pb-3">
                    <span className="font-medium">Contact:</span>
                    <div className="text-right text-sm">
                      <div>{orderData.customerName}</div>
                      <div>{orderData.customerEmail}</div>
                      <div>{orderData.customerPhone}</div>
                    </div>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-3">
                    <span>Total Price:</span>
                    <span className="text-[var(--accent-gold)]" data-testid="total-price">
                      ${calculatePrice(orderData).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  data-testid="submit-order-button"
                  className="w-full px-8 py-5 bg-[var(--accent-gold)] text-white rounded-full text-lg font-semibold button-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Order'}
                </button>
                
                <p className="text-sm text-[var(--text-muted)] text-center">
                  By submitting, you'll receive a confirmation email. We'll contact you to finalize details.
                </p>
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          {step < 7 && (
            <div className="flex justify-between mt-12 pt-8 border-t border-[var(--border-soft)]">
              <button
                onClick={handleBack}
                disabled={step === 1}
                data-testid="back-button"
                className="px-8 py-3 border-2 border-[var(--border-soft)] rounded-full font-medium button-hover disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <ChevronLeft size={20} />
                Back
              </button>
              
              {step < 6 && (
                <button
                  onClick={handleNext}
                  data-testid="next-button"
                  className="px-8 py-3 bg-[var(--primary)] text-[var(--primary-foreground)] rounded-full font-medium button-hover flex items-center gap-2"
                >
                  Next
                  <ChevronRight size={20} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
